import { indexGenerator } from '@trustoo/utils/indexGenerator'
import { createElement, Fragment, JSX, ReactNode } from 'react'

interface BasicMdParserProps {
    input?: string
    tagName?: keyof JSX.IntrinsicElements
    disallowLinks?: boolean
    suppressHydrationWarning?: boolean
}

interface ReplacementsMap {
    [key: string]: ReactNode
}

// Placeholder strings for replacement markers (should be unique and not used in the input string)
const REPLACEMENT_ID_PREFIX = '@@@'
const REPLACEMENT_SEPARATOR = '|||'

// Regular expressions for Markdown (a bit strict, but they work)
const linkRegex = /\[([^\[]+)]\(([^)]+)\)/g
const boldRegex = /\*\*(.*?)\*\*/gm
const headerRegex = /\*\*\*(.*?)\*\*\*/gm
const italicRegex = /\*(.*?)\*/gm

// Standard Markdown link format: [title](url)
const linkReplacer = ([, tagTitle, tagURL]: RegExpMatchArray) => (
    <a target="_blank" rel="noopener" href={tagURL} className="text-[#5788ff]">
        {tagTitle}
    </a>
)

const boldReplacer = ([, inner]: RegExpMatchArray) => <b>{inner}</b>

const headerReplacer = ([, inner]: RegExpMatchArray) => <h2>{inner}</h2>

const italicReplacer = ([, inner]: RegExpMatchArray) => <i>{inner}</i>

const replaceBreaks = (str: string): (ReactNode | null)[] => {
    const lines = str.split('\n')
    return lines.map((line, i) => {
        const isLast = lines.length === i + 1
        if (line.length === 0 && (isLast || lines[i + 1].length === 0)) {
            return null
        }
        return (
            <Fragment key={i}>
                {line}
                {!isLast && <br />}
            </Fragment>
        )
    })
}

/**
 * Consider replacing this function with a more robust Markdown parser library (for example: https://github.com/markedjs/marked)
 */
const replaceMarkdown = (str: string, disallowLinks: boolean): ReactNode[] => {
    const replacementsMap: ReplacementsMap = {}
    const idGenerator = indexGenerator()
    const replaceRegex = (
        regex: RegExp,
        replacement: (match: RegExpMatchArray) => ReactNode,
        str: string,
    ): string => {
        let replacedStr = str
        const allMatches: RegExpMatchArray[] = []
        let matches = regex.exec(str)

        while (matches !== null) {
            allMatches.push(matches)
            matches = regex.exec(str)
        }

        allMatches.forEach((match) => {
            if (match) {
                const replacementId = idGenerator.next().value.toString()
                replacementsMap[replacementId] = replacement(match)
                replacedStr = replacedStr.replace(
                    match[0],
                    `${REPLACEMENT_ID_PREFIX}${replacementId}${REPLACEMENT_SEPARATOR}`,
                )
            }
        })

        return replacedStr
    }

    // Replace the links (if not disallowed)
    let replacedStr = disallowLinks ? str : replaceRegex(linkRegex, linkReplacer, str)

    // NOTE: Order of replacements is important as asterisk characters are used in both header, bold and italic text
    // We process from most to least asterisks (*** → ** → *) to avoid incorrect matches

    // Replace the header text (***text***)
    replacedStr = replaceRegex(headerRegex, headerReplacer, replacedStr)

    // Replace the bold text (**text**)
    replacedStr = replaceRegex(boldRegex, boldReplacer, replacedStr)

    // Replace the italic text (*text*)
    replacedStr = replaceRegex(italicRegex, italicReplacer, replacedStr)

    return replacedStr.split(REPLACEMENT_SEPARATOR).map((part, i) => {
        const [text, replacementId] = part.split(REPLACEMENT_ID_PREFIX)
        return (
            <Fragment key={i}>
                {replaceBreaks(text)}
                {replacementId && <> {replacementsMap[replacementId]}</>}
            </Fragment>
        )
    })
}

/**
 * Basic Markdown parser component.
 * Parses simple Markdown syntax: ***header***, **bold**, *italic*, and [title](url) links.
 * Supports disabling links via disallowLinks prop.
 */
const BasicMdParser = ({
    input = '',
    tagName = 'div',
    disallowLinks = false,
    suppressHydrationWarning,
}: BasicMdParserProps) => {
    return createElement(
        tagName,
        {
            className: 'whitespace-pre-wrap [&_ul]:list-disc [&_ul]:ps-10',
            suppressHydrationWarning,
        },
        replaceMarkdown(input, disallowLinks),
    )
}

export default BasicMdParser
